import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Button,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  MenuItem,
  Select,
  InputLabel,
  Typography,
  Box,
  Divider,
} from "@mui/material";
import { PMDialog } from "../../../utils/PMDialog";
import { useTranslation } from "react-i18next";
import { billingPlanItemsApi } from "../../../../api/billing_plan_items";
import { customerinvoicesApi } from "../../../../api/customerinvoices";
import toast from "react-hot-toast";

export const InvoiceBillingPlanItemDialog = ({
  open,
  onClose,
  onSuccess,
  project,
  billingPlanItem,
  contractSum = 0,
  billedSum = 0,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [invoiceType, setInvoiceType] = useState("automatic"); // "automatic" or "manual"
  const [externalInvoice, setExternalInvoice] = useState(false);
  const [linkedInvoiceId, setLinkedInvoiceId] = useState("");
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [completedAmount, setCompletedAmount] = useState(0);
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [retentionAmount, setRetentionAmount] = useState(0);

  const computeRetentionDisplay = useCallback(
    (completed) => {
      if (!project?.remaining_amount_enabled) return 0;
      const base =
        project.remaining_amount_base === "contract" ? contractSum || 0 : completed || 0;
      const pct = project.remaining_amount_percentage || 10;
      return ((base || 0) * pct) / 100;
    },
    [
      contractSum,
      project?.remaining_amount_enabled,
      project?.remaining_amount_base,
      project?.remaining_amount_percentage,
    ]
  );

  const fetchAvailableInvoices = async () => {
    if (!project?.id) return;

    setLoadingInvoices(true);
    try {
      const response = await customerinvoicesApi.getCustomerInvoices({
        project_id: project.id,
        fields: "id,invoice_number,displayTitle,amount,invoice_date",
      });
      const invoices = response.data?.data || response.data || [];
      setAvailableInvoices(Array.isArray(invoices) ? invoices : []);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setAvailableInvoices([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const initialCompletedAmount = Number(billingPlanItem?.completed_amount) || 0;
    const initialInvoiceAmount = billingPlanItem?.amount ?? 0;
    setCompletedAmount(initialCompletedAmount);
    setInvoiceAmount(initialInvoiceAmount);
    setRetentionAmount(
      billingPlanItem?.remaining_amount != null
        ? parseFloat(billingPlanItem.remaining_amount) || 0
        : computeRetentionDisplay(initialCompletedAmount)
    );
  }, [
    open,
    billingPlanItem?.id,
    billingPlanItem?.completed_amount,
    billingPlanItem?.amount,
    billingPlanItem?.remaining_amount,
    computeRetentionDisplay,
  ]);

  useEffect(() => {
    if (!open || invoiceType !== "manual" || !project?.id) return;
    fetchAvailableInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoiceType, project?.id]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("no-NO", {
      style: "currency",
      currency: "NOK",
    }).format(amount || 0);
  };

  const handleSubmit = async () => {
    if (!billingPlanItem) return;

    setLoading(true);
    try {
      // Only send invoice type and related settings
      // Amounts are read-only and should be changed in billing plan, not here
      const params = {
        invoice_type: invoiceType,
      };

      if (invoiceType === "manual") {
        params.external_invoice = externalInvoice;
        if (linkedInvoiceId) {
          params.linked_customerinvoice_id = linkedInvoiceId;
        }
      }

      const response = await billingPlanItemsApi.invoiceBillingPlanItem(
        project.id,
        billingPlanItem.id,
        params
      );

      const body = response?.data;
      if (body && body.success === false) {
        const msg = Array.isArray(body.errors) ? body.errors[0] : body.errors;
        toast.error(
          typeof msg === "string" ? msg : t("project.billing_plan.invoice_failed"),
          { duration: 6000 }
        );
        return;
      }

      toast.success(
        body?.meta?.tripletex_sync === "synced"
          ? t("project.billing_plan.invoice_created_tripletex_ok")
          : t("project.billing_plan.invoice_created")
      );
      if (body?.meta?.tripletex_sync === "queued") {
        toast(t("project.billing_plan.tripletex_queued_hint"), { duration: 10000 });
      }
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Error invoicing billing plan item:", error);
      const errData = error?.response?.data;
      const first =
        Array.isArray(errData?.errors) ? errData.errors[0] : errData?.errors;
      toast.error(
        (typeof first === "string" ? first : null) ||
          t("project.billing_plan.invoice_failed"),
        { duration: 6000 }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("project.billing_plan.invoice_a_account_title")}
      maxWidth="md"
      fullWidth
    >
      <div className="space-y-4">
        {/* Amount Summary - Read-only Overview */}
        <Box sx={{ p: 3, bgcolor: "grey.50", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
            {t("project.billing_plan.summary_title")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("project.billing_plan.invoice_edit_hint")}
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t("project.billing_plan.completed_amount")}
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "primary.main" }}>
                {formatCurrency(completedAmount)}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t("project.billing_plan.billed_amount")}
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "success.main" }}>
                {formatCurrency(billedSum)}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t("project.billing_plan.remaining_amount")}
              </Typography>
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ color: retentionAmount > 0 ? "warning.main" : "text.secondary" }}
              >
                {formatCurrency(retentionAmount)}
              </Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {t("project.billing_plan.amount_to_invoice")}
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "info.main" }}>
                {formatCurrency(invoiceAmount)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* Invoice Type Selection */}
        <FormControl component="fieldset">
          <FormLabel component="legend">{t("project.billing_plan.invoice_type_label")}</FormLabel>
          <RadioGroup
            value={invoiceType}
            onChange={(e) => setInvoiceType(e.target.value)}
          >
            <FormControlLabel
              value="automatic"
              control={<Radio />}
              label={t("project.billing_plan.invoice_auto")}
            />
            <FormControlLabel
              value="manual"
              control={<Radio />}
              label={t("project.billing_plan.invoice_manual")}
            />
          </RadioGroup>
        </FormControl>

        {/* Manual Invoice Options */}
        {invoiceType === "manual" && (
          <div className="space-y-4">
            <FormControlLabel
              control={
                <Checkbox
                  checked={externalInvoice}
                  onChange={(e) => setExternalInvoice(e.target.checked)}
                />
              }
              label={t("project.billing_plan.external_invoice_checkbox")}
            />

            <FormControl fullWidth>
              <InputLabel>{t("project.billing_plan.link_invoice")}</InputLabel>
              <Select
                value={linkedInvoiceId}
                onChange={(e) => setLinkedInvoiceId(e.target.value)}
                label={t("project.billing_plan.link_invoice")}
                disabled={loadingInvoices}
              >
                <MenuItem value="">
                  <em>{t("project.billing_plan.link_invoice_none")}</em>
                </MenuItem>
                {availableInvoices.map((invoice) => (
                  <MenuItem key={invoice.id} value={invoice.id}>
                    {invoice.invoice_number} - {formatCurrency(invoice.amount)} (
                    {invoice.invoice_date
                      ? new Date(invoice.invoice_date).toLocaleDateString("no-NO")
                      : ""}
                    )
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}

        {/* Dialog Actions */}
        <div className="flex justify-end space-x-2 pt-4">
          <Button onClick={onClose} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? t("project.billing_plan.invoice_loading") : t("project.billing_plan.invoice_confirm")}
          </Button>
        </div>
      </div>
    </PMDialog>
  );
};

